import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.id !== article?.id)
    .slice(0, 3);

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
        <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">返回首页</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category?.name || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '吃瓜网',
      logo: {
        '@type': 'ImageObject',
        url: 'https://chigua.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://chigua.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://chigua.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        articleAuthor={article.author}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Link
              to={`/category/${article.category}`}
              className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-200 transition-colors duration-200"
            >
              {categoryName}
            </Link>
            {article.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-user-line text-accent-600 text-sm"></i>
              </div>
              <span className="font-medium text-foreground-700">{article.author}</span>
            </div>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
            </span>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.commentCount} 评论
            </span>
          </div>
        </header>

        <div className="mb-8 rounded-xl overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-auto object-cover max-h-[500px]"
            title={`${article.title} - 吃瓜网`}
          />
        </div>

        <div
          className="prose prose-lg max-w-none text-foreground-800 leading-relaxed mb-10 article-content"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="border-t border-background-200 pt-6 mb-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500 font-medium">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors duration-200 cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between py-4 border-t border-background-200 mb-10">
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-accent-100 text-foreground-600 hover:text-accent-700 transition-colors duration-200 text-sm cursor-pointer whitespace-nowrap">
              <i className="ri-thumb-up-line"></i>
              <span>点赞</span>
            </button>
            <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-700 transition-colors duration-200 text-sm cursor-pointer whitespace-nowrap">
              <i className="ri-share-forward-line"></i>
              <span>分享</span>
            </button>
            <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-secondary-100 text-foreground-600 hover:text-secondary-700 transition-colors duration-200 text-sm cursor-pointer whitespace-nowrap">
              <i className="ri-bookmark-line"></i>
              <span>收藏</span>
            </button>
          </div>
          <Link
            to={`/category/${article.category}`}
            className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
          >
            返回{categoryName} <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>

        {relatedArticles.length > 0 && (
          <section className="border-t border-background-200 pt-8" aria-label="相关文章">
            <h3 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200">
                      {rel.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1.5">{rel.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}